import { assertNever } from "@redotech/util/type";
import { createEnum } from "@redotech/zod-util/zod-enum";

export type ReturnTableColumnKey =
  | ReturnDataColumnKey
  | ReturnProductColumnKey
  | OrderProductColumnKey
  | WarrantyCustomerDataColumnKey
  | PurchasedExtendedWarrantyDataColumnKey;

// Order of column keys determines the order of columns in the table and CSV export
export enum ReturnDataColumnKey {
  ORDER_NUMBER = "analyticstotals:orderName",
  SHOPIFY_ORDER_ID = "shopify_order_id",
  ORDER_CREATED_AT = "order_created_at",
  SALES_CHANNEL = "SALES_CHANNEL",
  RETURN_ID = "_id",
  RETURN_CREATED_AT = "createdat",
  RETURN_TYPE = "RETURN_TYPE",
  APPROVEDAT = "APPROVEDAT",
  APPROVEDBY = "APPROVEDBY",
  RETURN_PROCESSED_AT = "COMPLETEDAT",
  PROCESSEDBY = "PROCESSEDBY",
  RETURN_STATUS = "status",
  RETURN_VALUE_NO_TAX_NO_ADJUSTMENT = "analyticstotals:returnValueNoTaxNoAdjustment",
  COMPENSATION_METHODS = "COMPENSATION_METHODS",
  RETURN_TAX = "analyticstotals:returnTax",
  RETURN_ADJUSTMENT = "analyticstotals:returnAdjustment",
  RETURN_TOTAL_VALUE = "analyticstotals:returnTotalValue",
  RETURN_EXCHANGE_VALUE = "analyticstotals:returnExchangeValue",
  RETURN_STORE_CREDIT_VALUE = "analyticstotals:returnStoreCreditValue",
  RETURN_REFUND_VALUE = "",
  EXCHANGE_ORDER_IDS = "EXCHANGE_ORDER_IDS",
  EXCHANGE_FOR_PRODUCT_TITLES = "EXCHANGE_FOR_PRODUCT_TITLES",
  EXCHANGE_FOR_PRODUCT_IDS = "EXCHANGE_FOR_PRODUCT_IDS",
  EXCHANGE_FOR_VARIANT_IDS = "EXCHANGE_FOR_VARIANT_IDS",
  VARIANT_EXCHANGE_TITLES = "VARIANT_EXCHANGE_TITLES",
  VARIANT_EXCHANGE_PRODUCT_IDS = "VARIANT_EXCHANGE_PRODUCT_IDS",
  VARIANT_EXCHANGE_VARIANT_IDS = "VARIANT_EXCHANGE_VARIANT_IDS",
  ORIGINAL_ORDER_ITEM_QUANTITY = "ORIGINAL_ORDER_ITEM_QUANTITY",
  RETURNED_PRODUCT_COUNT = "RETURNED_PRODUCT_COUNT",
  MEDIA = "MEDIA",
  GIFTCARD_CODES = "GIFTCARD_CODES",
  TAGS = "TAGS",
  ADVANCED_EXCHANGE_TITLES = "ADVANCED_EXCHANGE_TITLES",
  ADVANCED_EXCHANGE_VARIANT_IDS = "ADVANCED_EXCHANGE_VARIANT_IDS",
  CUSTOMER_COMMENTS = "CUSTOMER_COMMENTS",
  AI_RETURN_REASON_SUMMARIES = "AI_RETURN_REASON_SUMMARIES",
  REFERRAL_EMAIL = "REFERRAL_EMAIL",
  RETURN_TRACKING_NUMBERS = "TRACKING_NUMBERS",
  ORDER_TRACKING_NUMBERS = "ORDER_TRACKING_NUMBERS",
  SERVICE_LEVELS = "service_levels",
  MERCHANT_ADDRESS_NAMES = "merchant_address_names",
  MOST_RECENT_LABEL_UPDATE = "most_recent_label_update",
  LABEL_STATUS = "label_status",
  LABEL_COST = "label_cost",
  UPSELL_AMOUNT = "UPSELL_AMOUNT",
  CUSTOMER_SHIPPING_COST = "totals_fee",
  RETURN_TOTAL_BONUSES = "atv2_return_total_bonuses",
  RETURN_TOTAL_FEES = "atv2_return_total_fees",
  RETURN_TOTAL_ADJUSTMENTS = "atv2_return_total_adjustments",
  ORDER_COUNTRY_CODE = "ORDER_COUNTRY_CODE",
  RETURN_TO_MERCHANT_DELIVERED_DATE = "return_to_merchant_delivered_date",
  PRODUCT_TITLES = "PRODUCT_TITLES",
  VARIANT_TITLES = "VARIANT_TITLES",
  PRODUCT_SKUS = "PRODUCT_SKUS",
  PRODUCT_STATUSES = "PRODUCT_STATUSES",
  PRODUCT_REASONS = "PRODUCT_REASONS",
  PRODUCT_IDS = "PRODUCT_IDS",
  VARIANT_IDS = "VARIANT_IDS",
  PRODUCT_REJECT_REASONS = "PRODUCT_REJECT_REASONS",
  RETURN_METHOD = "RETURN_METHOD",
  SHIPPING_METHOD = "SHIPPING_METHOD",
  PRODUCT_PRICES = "PRODUCT_PRICES",
  COVERED = "COVERED",
  CUSTOMER_EMAIL = "CUSTOMER_EMAIL",
  DISPOSITION_MERCHANT_NOTES = "DISPOSITION_MERCHANT_NOTES",
  DISPOSITION_MERCHANT_GRADES = "DISPOSITION_MERCHANT_GRADES",
  DISPOSITION_MERCHANT_OUTCOMES = "DISPOSITION_MERCHANT_OUTCOMES",
  FULFILLMENT_LOCATION_IDS = "FULFILLMENT_LOCATION_IDS",
  VENDORS = "VENDORS",
  CREATED_BY = "CREATED_BY",
  RETURN_CREATED_BY = "RETURN_CREATED_BY",
  RETURN_ASSESSMENTS = "RETURN_ASSESSMENTS",
  WARRANTY_ASSESSMENTS = "WARRANTY_ASSESSMENTS",
}
export function returnDataColumnSorterSlow(
  a: ReturnDataColumnKey,
  b: ReturnDataColumnKey,
): number {
  return Object.values(ReturnDataColumnKey).indexOf(a) <
    Object.values(ReturnDataColumnKey).indexOf(b)
    ? -1
    : 1;
}

export const ReturnDataColumnKeySchema = createEnum(
  Object.values(ReturnDataColumnKey) as [string, ...string[]],
);

export function getReturnDataColumnDisplayName(
  columnKey: ReturnDataColumnKey,
): string {
  switch (columnKey) {
    case ReturnDataColumnKey.RETURN_TYPE:
      return "Return Type";
    case ReturnDataColumnKey.RETURN_CREATED_AT:
      return "Created At";
    case ReturnDataColumnKey.RETURN_ID:
      return "Return ID";
    case ReturnDataColumnKey.ORDER_NUMBER:
      return "Order Number(s)";
    case ReturnDataColumnKey.SHOPIFY_ORDER_ID:
      return "Provider Order ID(s)";
    case ReturnDataColumnKey.ORDER_CREATED_AT:
      return "Order(s) Created At";
    case ReturnDataColumnKey.COMPENSATION_METHODS:
      return "Compensation Methods";
    case ReturnDataColumnKey.RETURN_STATUS:
      return "Return Status";
    case ReturnDataColumnKey.RETURN_VALUE_NO_TAX_NO_ADJUSTMENT:
      return "Return Value (No Tax/Adjustment)";
    case ReturnDataColumnKey.RETURN_TAX:
      return "Return Tax";
    case ReturnDataColumnKey.RETURN_ADJUSTMENT:
      return "Return Adjustment";
    case ReturnDataColumnKey.RETURN_TOTAL_VALUE:
      return "Return Total Value";
    case ReturnDataColumnKey.RETURN_REFUND_VALUE:
      return "Return Refund Value";
    case ReturnDataColumnKey.RETURN_EXCHANGE_VALUE:
      return "Return Exchange Value";
    case ReturnDataColumnKey.RETURN_STORE_CREDIT_VALUE:
      return "Return Store Credit Value";
    case ReturnDataColumnKey.RETURN_PROCESSED_AT:
      return "Processed At";
    case ReturnDataColumnKey.MEDIA:
      return "Customer Images";
    case ReturnDataColumnKey.APPROVEDAT:
      return "Approved At";
    case ReturnDataColumnKey.APPROVEDBY:
      return "Approved By";
    case ReturnDataColumnKey.PROCESSEDBY:
      return "Processed By";
    case ReturnDataColumnKey.SALES_CHANNEL:
      return "Sales Channel(s)";
    case ReturnDataColumnKey.GIFTCARD_CODES:
      return "Gift Card Codes";
    case ReturnDataColumnKey.TAGS:
      return "Tags";
    case ReturnDataColumnKey.VARIANT_EXCHANGE_PRODUCT_IDS:
      return "Variant Exchange Product IDs";
    case ReturnDataColumnKey.VARIANT_EXCHANGE_TITLES:
      return "Variant Exchange Titles";
    case ReturnDataColumnKey.VARIANT_EXCHANGE_VARIANT_IDS:
      return "Variant Exchange Variant IDs";
    case ReturnDataColumnKey.EXCHANGE_FOR_PRODUCT_TITLES:
      return "Exchange For Product Titles";
    case ReturnDataColumnKey.EXCHANGE_FOR_PRODUCT_IDS:
      return "Exchange For Product IDs";
    case ReturnDataColumnKey.EXCHANGE_FOR_VARIANT_IDS:
      return "Exchange For Variant IDs";
    case ReturnDataColumnKey.RETURNED_PRODUCT_COUNT:
      return "Returned Product Count";
    case ReturnDataColumnKey.EXCHANGE_ORDER_IDS:
      return "Exchange Order IDs";
    case ReturnDataColumnKey.ORIGINAL_ORDER_ITEM_QUANTITY:
      return "Original Order Item Quantity";
    case ReturnDataColumnKey.ADVANCED_EXCHANGE_TITLES:
      return "Advanced Exchange Titles";
    case ReturnDataColumnKey.ADVANCED_EXCHANGE_VARIANT_IDS:
      return "Advanced Exchange Variant IDs";
    case ReturnDataColumnKey.CUSTOMER_COMMENTS:
      return "Customer Comments";
    case ReturnDataColumnKey.AI_RETURN_REASON_SUMMARIES:
      return "AI Return Reason Summaries";
    case ReturnDataColumnKey.REFERRAL_EMAIL:
      return "Referral Email";
    case ReturnDataColumnKey.RETURN_TRACKING_NUMBERS:
      return "Return Tracking Numbers";
    case ReturnDataColumnKey.ORDER_TRACKING_NUMBERS:
      return "Order Tracking Numbers";
    case ReturnDataColumnKey.SERVICE_LEVELS:
      return "Service Levels";
    case ReturnDataColumnKey.MERCHANT_ADDRESS_NAMES:
      return "Merchant Address Names";
    case ReturnDataColumnKey.MOST_RECENT_LABEL_UPDATE:
      return "Most Recent Label Update";
    case ReturnDataColumnKey.LABEL_STATUS:
      return "Label Status";
    case ReturnDataColumnKey.LABEL_COST:
      return "Label Cost";
    case ReturnDataColumnKey.UPSELL_AMOUNT:
      return "Upsell Amount";
    case ReturnDataColumnKey.CUSTOMER_SHIPPING_COST:
      return "Customer Shipping Cost";
    case ReturnDataColumnKey.RETURN_TOTAL_BONUSES:
      return "Return Total Bonuses";
    case ReturnDataColumnKey.RETURN_TOTAL_FEES:
      return "Return Total Fees";
    case ReturnDataColumnKey.RETURN_TOTAL_ADJUSTMENTS:
      return "Return Total Adjustments";
    case ReturnDataColumnKey.ORDER_COUNTRY_CODE:
      return "Order Country Code(s)";
    case ReturnDataColumnKey.RETURN_TO_MERCHANT_DELIVERED_DATE:
      return "Return To Merchant Delivered Date";
    case ReturnDataColumnKey.PRODUCT_TITLES:
      return "Product Titles";
    case ReturnDataColumnKey.VARIANT_TITLES:
      return "Variant Titles";
    case ReturnDataColumnKey.PRODUCT_SKUS:
      return "Product SKUs";
    case ReturnDataColumnKey.PRODUCT_STATUSES:
      return "Product Statuses";
    case ReturnDataColumnKey.PRODUCT_REASONS:
      return "Product Reasons";
    case ReturnDataColumnKey.PRODUCT_IDS:
      return "Product IDs";
    case ReturnDataColumnKey.VARIANT_IDS:
      return "Variant IDs";
    case ReturnDataColumnKey.PRODUCT_REJECT_REASONS:
      return "Product Reject Reasons";
    case ReturnDataColumnKey.RETURN_METHOD:
      return "Return Method";
    case ReturnDataColumnKey.SHIPPING_METHOD:
      return "Shipping Method";
    case ReturnDataColumnKey.PRODUCT_PRICES:
      return "Product Prices";
    case ReturnDataColumnKey.COVERED:
      return "Covered";
    case ReturnDataColumnKey.CUSTOMER_EMAIL:
      return "Customer Email";
    case ReturnDataColumnKey.DISPOSITION_MERCHANT_NOTES:
      return "Disposition Merchant Notes";
    case ReturnDataColumnKey.DISPOSITION_MERCHANT_GRADES:
      return "Disposition Merchant Grades";
    case ReturnDataColumnKey.DISPOSITION_MERCHANT_OUTCOMES:
      return "Disposition Merchant Outcomes";
    case ReturnDataColumnKey.FULFILLMENT_LOCATION_IDS:
      return "Fulfillment Location IDs";
    case ReturnDataColumnKey.VENDORS:
      return "Vendors";
    case ReturnDataColumnKey.CREATED_BY:
      return "Created By";
    case ReturnDataColumnKey.RETURN_CREATED_BY:
      return "Return Created By";
    case ReturnDataColumnKey.RETURN_ASSESSMENTS:
      return "Return Assessments";
    case ReturnDataColumnKey.WARRANTY_ASSESSMENTS:
      return "Warranty Assessments";
    default:
      assertNever(columnKey);
  }
}

export enum ReturnProductColumnKey {
  RETURN_TYPE = "RETURN_TYPE",
  RETURN_ID = "return_id",
  CREATED_AT = "created_at",
  ORDER_NUMBER = "return_analytics_totals:orderName",
  ORDER_CREATED_AT = "order_created_at",
  PRODUCT_NAME = "product_title",
  VARIANT_NAME = "variant_title",
  SKU = "sku",
  UPC = "upc",
  QUANTITY = "quantity",
  PRODUCT_STATUS = "status",
  RETURN_STATUS = "return_status",
  SHOPIFY_ORDER_ID = "shopify_order_id",
  COMPENSATION_METHOD = "COMPENSATION_METHOD",
  REASON = "reason",
  PRIMARY_REASON = "primary_reason",
  SECONDARY_REASON = "secondary_reason",
  REASON_CODE = "reason_code",
  PRIMARY_REASON_CODE = "primary_reason_code",
  SECONDARY_REASON_CODE = "secondary_reason_code",
  SHOPIFY_PRODUCT_ID = "product_id",
  SHOPIFY_VARIANT_ID = "variant_id",
  PRODUCT_VALUE_NO_TAX_NO_ADJUSTMENT = "product_analytics_totals:productValueNoTaxNoAdjustment",
  PRODUCT_TAX = "product_analytics_totals:productTax",
  PRODUCT_ADJUSTMENT = "product_analytics_totals:productAdjustment",
  PRODUCT_TOTAL_VALUE = "product_analytics_totals:productTotalValue",
  PRODUCT_EXCHANGE_VALUE = "product_analytics_totals:productExchangeValue",
  PRODUCT_STORE_CREDIT_VALUE = "product_analytics_totals:productStoreCreditValue",
  PRODUCT_REFUND_VALUE = "product_analytics_totals:productRefundValue",
  PROCESSED_BY = "return_operations:processedBy",
  RETURN_PROCESSED_AT = "return_operation:processedAt",
  APPROVED_BY = "return_operation:approvedBy",
  RETURN_APPROVED_AT = "return_operation:approvedAt",
  PRODUCT_PROCESSED_AT = "productProcessedAt",
  PRODUCT_APPROVED_AT = "productApprovedAt",
  REJECT_REASON = "reject_reason",
  RETURN_METHOD = "return_method",
  SHIPPING_METHOD = "shipping_method",
  CREATED_BY = "return_operation:createdBy",
  PRICE = "PRICE",
  SALES_CHANNEL = "SALES_CHANNEL",
  COVERED = "COVERED",
  CUSTOMER_EMAIL = "CUSTOMER_EMAIL",
  DISPOSITION_MERCHANT_NOTES = "DISPOSITION_MERCHANT_NOTES",
  DISPOSITION_MERCHANT_GRADE = "DISPOSITION_MERCHANT_GRADE",
  DISPOSITION_MERCHANT_OUTCOME = "DISPOSITION_MERCHANT_OUTCOME",
  CUSTOMER_IMAGES = "CUSTOMER_IMAGES",
  FULFILLMENT_LOCATION_ID = "fulfillment_location_id",
  RETURN_TO_MERCHANT_DELIVERED_DATE = "return_to_merchant_delivered_date",
  CUSTOMER_COMMENTS = "CUSTOMER_COMMENTS",
  AI_RETURN_REASON_SUMMARY = "AI_RETURN_REASON_SUMMARY",
  RETURN_TRACKING_NUMBERS = "TRACKING_NUMBERS",
  ORDER_TRACKING_NUMBERS = "ORDER_TRACKING_NUMBERS",
  VENDOR = "VENDOR",
  TAGS = "TAGS",
  ORDER_COUNTRY_CODE = "ORDER_COUNTRY_CODE",
  RETURN_CREATED_BY = "RETURN_CREATED_BY",
  RETURN_ASSESSMENTS = "RETURN_ASSESSMENTS",
  WARRANTY_ASSESSMENTS = "WARRANTY_ASSESSMENTS",
  LABEL_STATUS = "label_status",
  MOST_RECENT_LABEL_UPDATE = "most_recent_label_update",
}
export function returnedProductColumnSorterSlow(
  a: ReturnProductColumnKey,
  b: ReturnProductColumnKey,
): number {
  return Object.values(ReturnProductColumnKey).indexOf(a) <
    Object.values(ReturnProductColumnKey).indexOf(b)
    ? -1
    : 1;
}

export function getReturnedProductColumnDisplayName(
  columnKey: ReturnProductColumnKey,
): string {
  switch (columnKey) {
    case ReturnProductColumnKey.RETURN_TYPE:
      return "Return Type";
    case ReturnProductColumnKey.RETURN_ID:
      return "Return ID";
    case ReturnProductColumnKey.CREATED_AT:
      return "Created At";
    case ReturnProductColumnKey.ORDER_NUMBER:
      return "Order Number";
    case ReturnProductColumnKey.ORDER_CREATED_AT:
      return "Order Created At";
    case ReturnProductColumnKey.PRODUCT_NAME:
      return "Product Name";
    case ReturnProductColumnKey.VARIANT_NAME:
      return "Variant Name";
    case ReturnProductColumnKey.SKU:
      return "SKU";
    case ReturnProductColumnKey.UPC:
      return "UPC";
    case ReturnProductColumnKey.QUANTITY:
      return "Quantity";
    case ReturnProductColumnKey.PRODUCT_STATUS:
      return "Product Status";
    case ReturnProductColumnKey.RETURN_STATUS:
      return "Return Status";
    case ReturnProductColumnKey.SHOPIFY_ORDER_ID:
      return "Provider Order ID";
    case ReturnProductColumnKey.COMPENSATION_METHOD:
      return "Compensation Method";
    case ReturnProductColumnKey.REASON:
      return "Reason";
    case ReturnProductColumnKey.PRIMARY_REASON:
      return "Primary Reason";
    case ReturnProductColumnKey.SECONDARY_REASON:
      return "Secondary Reason";
    case ReturnProductColumnKey.REASON_CODE:
      return "Reason Code";
    case ReturnProductColumnKey.PRIMARY_REASON_CODE:
      return "Primary Reason Code";
    case ReturnProductColumnKey.SECONDARY_REASON_CODE:
      return "Secondary Reason Code";
    case ReturnProductColumnKey.SHOPIFY_PRODUCT_ID:
      return "Shopify Product ID";
    case ReturnProductColumnKey.SHOPIFY_VARIANT_ID:
      return "Shopify Variant ID";
    case ReturnProductColumnKey.PRODUCT_VALUE_NO_TAX_NO_ADJUSTMENT:
      return "Product Value (No Tax/Adjustment)";
    case ReturnProductColumnKey.PRODUCT_TAX:
      return "Product Tax";
    case ReturnProductColumnKey.PRODUCT_ADJUSTMENT:
      return "Product Adjustment";
    case ReturnProductColumnKey.PRODUCT_TOTAL_VALUE:
      return "Product Total Value";
    case ReturnProductColumnKey.PRODUCT_EXCHANGE_VALUE:
      return "Product Exchange Value";
    case ReturnProductColumnKey.PRODUCT_STORE_CREDIT_VALUE:
      return "Product Store Credit Value";
    case ReturnProductColumnKey.PRODUCT_REFUND_VALUE:
      return "Product Refund Value";
    case ReturnProductColumnKey.REJECT_REASON:
      return "Reject Reason";
    case ReturnProductColumnKey.RETURN_METHOD:
      return "Return Method";
    case ReturnProductColumnKey.SHIPPING_METHOD:
      return "Shipping Method";
    case ReturnProductColumnKey.PROCESSED_BY:
      return "Processed By";
    case ReturnProductColumnKey.RETURN_PROCESSED_AT:
      return "Return Processed At";
    case ReturnProductColumnKey.APPROVED_BY:
      return "Approved By";
    case ReturnProductColumnKey.RETURN_APPROVED_AT:
      return "Return Approved At";
    case ReturnProductColumnKey.PRODUCT_PROCESSED_AT:
      return "Product Processed At";
    case ReturnProductColumnKey.PRODUCT_APPROVED_AT:
      return "Product Approved At";
    case ReturnProductColumnKey.CREATED_BY:
      return "Created By";
    case ReturnProductColumnKey.PRICE:
      return "Price";
    case ReturnProductColumnKey.SALES_CHANNEL:
      return "Sales Channel";
    case ReturnProductColumnKey.COVERED:
      return "Covered";
    case ReturnProductColumnKey.CUSTOMER_EMAIL:
      return "Customer Email";
    case ReturnProductColumnKey.CUSTOMER_IMAGES:
      return "Customer Images";
    case ReturnProductColumnKey.DISPOSITION_MERCHANT_GRADE:
      return "Disposition Merchant Grade";
    case ReturnProductColumnKey.DISPOSITION_MERCHANT_NOTES:
      return "Disposition Merchant Notes";
    case ReturnProductColumnKey.DISPOSITION_MERCHANT_OUTCOME:
      return "Disposition Merchant Outcome";
    case ReturnProductColumnKey.FULFILLMENT_LOCATION_ID:
      return "Fulfillment Location ID";
    case ReturnProductColumnKey.RETURN_TO_MERCHANT_DELIVERED_DATE:
      return "Return To Merchant Delivered Date";
    case ReturnProductColumnKey.CUSTOMER_COMMENTS:
      return "Customer Comments";
    case ReturnProductColumnKey.AI_RETURN_REASON_SUMMARY:
      return "AI Return Reason Summary";
    case ReturnProductColumnKey.RETURN_TRACKING_NUMBERS:
      return "Return Tracking Numbers";
    case ReturnProductColumnKey.ORDER_TRACKING_NUMBERS:
      return "Order Tracking Numbers";
    case ReturnProductColumnKey.VENDOR:
      return "Vendor";
    case ReturnProductColumnKey.TAGS:
      return "Tags";
    case ReturnProductColumnKey.ORDER_COUNTRY_CODE:
      return "Order Country Code";
    case ReturnProductColumnKey.RETURN_CREATED_BY:
      return "Return Created By";
    case ReturnProductColumnKey.RETURN_ASSESSMENTS:
      return "Return Assessments";
    case ReturnProductColumnKey.WARRANTY_ASSESSMENTS:
      return "Warranty Assessments";
    case ReturnProductColumnKey.LABEL_STATUS:
      return "Label Status";
    case ReturnProductColumnKey.MOST_RECENT_LABEL_UPDATE:
      return "Most Recent Label Update";

    default:
      assertNever(columnKey);
  }
}

// Order of column keys determines the order of columns in the table and CSV export
export enum OrderProductColumnKey {
  ORDER_NAME = "order_name",
  SHOPIFY_ORDER_ID = "shopify_order_id",
  ORDER_PROCESSED_AT = "order_processed_at",
  ORDER_CANCELLED_AT = "order_cancelled_at",
  ORDER_PROCESSED_DATE = "order_processed_date",
  ORDER_CANCELLED_DATE = "order_cancelled_date",
  SALES_CHANNEL = "sales_channel",
  LINE_ITEM_ID = "line_item_id",
  PRODUCT_ID = "product_id",
  VARIANT_ID = "variant_id",
  PRODUCT_TITLE = "product_title",
  VARIANT_TITLE = "variant_title",
  SKU = "sku",
  UPC = "upc",
  PRODUCT_TYPE = "product_type",
  PRODUCT_TAGS = "product_tags",
  VENDOR = "vendor",
  TAGS = "tags",
  QUANTITY = "quantity",
  UNIT_PRICE = "unit_price",
  PRICE = "price",
  DISCOUNT_ALLOCATIONS = "discount_allocations",
  TAX_PRICE = "tax_price",
  CUSTOMER_EMAIL = "customer_email",
  CUSTOMER_NAME = "customer_name",
  IS_CUSTOMER_FIRST_ORDER = "is_customer_first_order",
  ORDER_COUNTRY_CODE = "order_country_code",
  FINANCIAL_STATUS = "financial_status",
  IS_EXCHANGE_ORDER = "is_exchange_order",
  IS_PROTECTED = "is_protected",
  IS_REDO_ELIGIBLE = "is_redo_eligible",
  IS_PACKAGE_PROTECTED = "is_package_protected",
  FULFILLMENT_LOCATION_ID = "fulfillment_location_id",
  ORDER_TRACKING_NUMBERS = "order_tracking_numbers",
  FULFILLMENT_SHIPMENT_STATUS = "fulfillment_shipment_status",
  ORDER_ITEM_DELIVERED_AT = "order_item_delivered_at",
  ORDER_ITEM_DELIVERED_DATE = "order_item_delivered_date",
  TOTAL_QUANTITY_RETURNED = "total_quantity_returned",
  TOTAL_RETURN_PRICE = "total_return_price",
  TOTAL_RETURN_REFUND_VALUE = "total_return_refund_value",
  TOTAL_RETURN_STORE_CREDIT_VALUE = "total_return_store_credit_value",
  TOTAL_RETURN_EXCHANGE_VALUE = "total_return_exchange_value",
  TOTAL_VARIANT_EXCHANGE_VALUE = "total_variant_exchange_value",
  TOTAL_ADVANCED_EXCHANGE_VALUE = "total_advanced_exchange_value",
  TOTAL_RETURN_TAX = "total_return_tax",
  TOTAL_RETURN_ADJUSTMENT = "total_return_adjustment",
  TOTAL_RETURN_PRODUCT_VALUE = "total_return_product_value",
  TOTAL_RETURN_VALUE_NO_TAX_NO_ADJUSTMENT = "total_return_value_no_tax_no_adjustment",
  HAS_PRODUCT_VARIANT_EXCHANGE = "has_product_variant_exchange",
  HAS_PRODUCT_ADVANCED_EXCHANGE = "has_product_advanced_exchange",
  RETURN_IDS = "return_ids",
  RETURN_LINE_ITEM_IDS = "return_line_item_ids",
  COMPENSATION_METHODS = "compensation_methods",
  RETURN_TYPES = "return_types",
  RETURN_REASONS = "return_reasons",
  RETURN_METHODS = "return_methods",
  RETURN_PRODUCT_STATUSES = "return_product_statuses",
  EXCHANGE_GROUP_ITEMS = "exchange_group_items",
  EXCHANGE_FOR_ITEMS = "exchange_for_items",
  EXCHANGE_TYPES = "exchange_types",
  ADVANCED_EXCHANGE_ITEMS = "advanced_exchange_items",
  RETURN_TO_MERCHANT_DELIVERED_DATES = "return_to_merchant_delivered_dates",
  RETURN_REJECT_REASONS = "return_reject_reasons",
  DISPOSITION_MERCHANT_NOTES = "disposition_merchant_notes",
  DISPOSITION_MERCHANT_GRADES = "disposition_merchant_grades",
  DISPOSITION_MERCHANT_OUTCOMES = "disposition_merchant_outcomes",
}

export function orderProductColumnSorterSlow(
  a: OrderProductColumnKey,
  b: OrderProductColumnKey,
): number {
  return Object.values(OrderProductColumnKey).indexOf(a) <
    Object.values(OrderProductColumnKey).indexOf(b)
    ? -1
    : 1;
}

export function getOrderProductColumnDisplayName(
  columnKey: OrderProductColumnKey,
): string {
  switch (columnKey) {
    case OrderProductColumnKey.ORDER_NAME:
      return "Order Number";
    case OrderProductColumnKey.SHOPIFY_ORDER_ID:
      return "Provider Order ID";
    case OrderProductColumnKey.ORDER_PROCESSED_AT:
      return "Order Processed At";
    case OrderProductColumnKey.ORDER_CANCELLED_AT:
      return "Order Cancelled At";
    case OrderProductColumnKey.ORDER_PROCESSED_DATE:
      return "Order Processed Date";
    case OrderProductColumnKey.ORDER_CANCELLED_DATE:
      return "Order Cancelled Date";
    case OrderProductColumnKey.SALES_CHANNEL:
      return "Sales Channel";
    case OrderProductColumnKey.LINE_ITEM_ID:
      return "Line Item ID";
    case OrderProductColumnKey.PRODUCT_ID:
      return "Product ID";
    case OrderProductColumnKey.VARIANT_ID:
      return "Variant ID";
    case OrderProductColumnKey.PRODUCT_TITLE:
      return "Product Name";
    case OrderProductColumnKey.VARIANT_TITLE:
      return "Variant Name";
    case OrderProductColumnKey.SKU:
      return "SKU";
    case OrderProductColumnKey.UPC:
      return "UPC";
    case OrderProductColumnKey.PRODUCT_TYPE:
      return "Product Type";
    case OrderProductColumnKey.PRODUCT_TAGS:
      return "Product Tags";
    case OrderProductColumnKey.VENDOR:
      return "Vendor";
    case OrderProductColumnKey.TAGS:
      return "Tags";
    case OrderProductColumnKey.QUANTITY:
      return "Quantity";
    case OrderProductColumnKey.UNIT_PRICE:
      return "Unit Price";
    case OrderProductColumnKey.PRICE:
      return "Price";
    case OrderProductColumnKey.DISCOUNT_ALLOCATIONS:
      return "Discount Allocations";
    case OrderProductColumnKey.TAX_PRICE:
      return "Tax";
    case OrderProductColumnKey.CUSTOMER_EMAIL:
      return "Customer Email";
    case OrderProductColumnKey.CUSTOMER_NAME:
      return "Customer Name";
    case OrderProductColumnKey.IS_CUSTOMER_FIRST_ORDER:
      return "Is Customer First Order";
    case OrderProductColumnKey.ORDER_COUNTRY_CODE:
      return "Order Country Code";
    case OrderProductColumnKey.FINANCIAL_STATUS:
      return "Financial Status";
    case OrderProductColumnKey.IS_EXCHANGE_ORDER:
      return "Is Exchange Order";
    case OrderProductColumnKey.IS_PROTECTED:
      return "Is Protected";
    case OrderProductColumnKey.IS_REDO_ELIGIBLE:
      return "Is Redo Eligible";
    case OrderProductColumnKey.IS_PACKAGE_PROTECTED:
      return "Is Package Protected";
    case OrderProductColumnKey.FULFILLMENT_LOCATION_ID:
      return "Fulfillment Location ID";
    case OrderProductColumnKey.ORDER_TRACKING_NUMBERS:
      return "Order Tracking Numbers";
    case OrderProductColumnKey.FULFILLMENT_SHIPMENT_STATUS:
      return "Fulfillment Shipment Status";
    case OrderProductColumnKey.ORDER_ITEM_DELIVERED_AT:
      return "Order Item Delivered At";
    case OrderProductColumnKey.ORDER_ITEM_DELIVERED_DATE:
      return "Order Item Delivered Date";
    case OrderProductColumnKey.TOTAL_QUANTITY_RETURNED:
      return "Total Quantity Returned";
    case OrderProductColumnKey.TOTAL_RETURN_PRICE:
      return "Total Return Price";
    case OrderProductColumnKey.TOTAL_RETURN_REFUND_VALUE:
      return "Total Return Refund Value";
    case OrderProductColumnKey.TOTAL_RETURN_STORE_CREDIT_VALUE:
      return "Total Return Store Credit Value";
    case OrderProductColumnKey.TOTAL_RETURN_EXCHANGE_VALUE:
      return "Total Return Exchange Value";
    case OrderProductColumnKey.TOTAL_VARIANT_EXCHANGE_VALUE:
      return "Total Variant Exchange Value";
    case OrderProductColumnKey.TOTAL_ADVANCED_EXCHANGE_VALUE:
      return "Total Advanced Exchange Value";
    case OrderProductColumnKey.TOTAL_RETURN_TAX:
      return "Total Return Tax";
    case OrderProductColumnKey.TOTAL_RETURN_ADJUSTMENT:
      return "Total Return Adjustment";
    case OrderProductColumnKey.TOTAL_RETURN_PRODUCT_VALUE:
      return "Total Return Product Value";
    case OrderProductColumnKey.TOTAL_RETURN_VALUE_NO_TAX_NO_ADJUSTMENT:
      return "Total Return Value (No Tax/Adjustment)";
    case OrderProductColumnKey.HAS_PRODUCT_VARIANT_EXCHANGE:
      return "Has Product Variant Exchange";
    case OrderProductColumnKey.HAS_PRODUCT_ADVANCED_EXCHANGE:
      return "Has Product Advanced Exchange";
    case OrderProductColumnKey.RETURN_IDS:
      return "Return IDs";
    case OrderProductColumnKey.RETURN_LINE_ITEM_IDS:
      return "Return Line Item IDs";
    case OrderProductColumnKey.COMPENSATION_METHODS:
      return "Compensation Methods";
    case OrderProductColumnKey.RETURN_TYPES:
      return "Return Types";
    case OrderProductColumnKey.RETURN_REASONS:
      return "Return Reasons";
    case OrderProductColumnKey.RETURN_METHODS:
      return "Return Methods";
    case OrderProductColumnKey.RETURN_PRODUCT_STATUSES:
      return "Return Product Statuses";
    case OrderProductColumnKey.EXCHANGE_GROUP_ITEMS:
      return "Exchange Group Items";
    case OrderProductColumnKey.EXCHANGE_FOR_ITEMS:
      return "Exchange For Items";
    case OrderProductColumnKey.EXCHANGE_TYPES:
      return "Exchange Types";
    case OrderProductColumnKey.ADVANCED_EXCHANGE_ITEMS:
      return "Advanced Exchange Items";
    case OrderProductColumnKey.RETURN_TO_MERCHANT_DELIVERED_DATES:
      return "Return to Merchant Delivered Dates";
    case OrderProductColumnKey.RETURN_REJECT_REASONS:
      return "Return Reject Reasons";
    case OrderProductColumnKey.DISPOSITION_MERCHANT_NOTES:
      return "Disposition Merchant Notes";
    case OrderProductColumnKey.DISPOSITION_MERCHANT_GRADES:
      return "Disposition Merchant Grades";
    case OrderProductColumnKey.DISPOSITION_MERCHANT_OUTCOMES:
      return "Disposition Merchant Outcomes";
    default:
      assertNever(columnKey);
  }
}

export enum WarrantyCustomerDataColumnKey {
  CUSTOMER_NAME = "customerName",
  CUSTOMER_EMAIL = "customerEmail",
  WARRANTY_NAME = "warrantyName",
  SOURCE = "source",
  SOURCE_NAME = "channelName",
  ORDER_NUMBER = "orderNumber",
  CUSTOMER_PHONE = "customerPhone",
  CUSTOMER_ADDRESS = "customerAddress",
  CUSTOMER_SECOND_ADDRESS = "customerSecondAddress",
  CUSTOMER_CITY = "customerCity",
  CUSTOMER_PROVINCE = "customerProvince",
  CUSTOMER_POSTAL = "customerPostal",
  PRODUCT_NAME = "productName",
  VARIANT_NAME = "variantName",
  WARRANTY_REGISTRATION_DATE = "warrantyRegistrationDate",
  OPTED_INTO_MARKETING = "optedIntoMarketing",
}
export function warrantyCustomerDataColumnSorterSlow(
  a: WarrantyCustomerDataColumnKey,
  b: WarrantyCustomerDataColumnKey,
): number {
  return Object.values(WarrantyCustomerDataColumnKey).indexOf(a) <
    Object.values(WarrantyCustomerDataColumnKey).indexOf(b)
    ? -1
    : 1;
}

const columnKeyToDisplayNameMap: Record<WarrantyCustomerDataColumnKey, string> =
  {
    [WarrantyCustomerDataColumnKey.CUSTOMER_NAME]: "Customer Name",
    [WarrantyCustomerDataColumnKey.CUSTOMER_EMAIL]: "Customer Email",
    [WarrantyCustomerDataColumnKey.WARRANTY_NAME]: "Warranty Name",
    [WarrantyCustomerDataColumnKey.CUSTOMER_PHONE]: "Customer Phone",
    [WarrantyCustomerDataColumnKey.CUSTOMER_ADDRESS]: "Customer Address",
    [WarrantyCustomerDataColumnKey.CUSTOMER_SECOND_ADDRESS]:
      "Customer Second Address",
    [WarrantyCustomerDataColumnKey.CUSTOMER_CITY]: "Customer City",
    [WarrantyCustomerDataColumnKey.CUSTOMER_PROVINCE]: "Customer Province",
    [WarrantyCustomerDataColumnKey.CUSTOMER_POSTAL]: "Customer Postal Code",
    [WarrantyCustomerDataColumnKey.PRODUCT_NAME]: "Product Name",
    [WarrantyCustomerDataColumnKey.VARIANT_NAME]: "Variant Name",
    [WarrantyCustomerDataColumnKey.SOURCE]: "Source",
    [WarrantyCustomerDataColumnKey.SOURCE_NAME]: "Source Name",
    [WarrantyCustomerDataColumnKey.ORDER_NUMBER]: "Order#",
    [WarrantyCustomerDataColumnKey.WARRANTY_REGISTRATION_DATE]:
      "Warranty Registration Date",
    [WarrantyCustomerDataColumnKey.OPTED_INTO_MARKETING]:
      "Opted Into Marketing",
  };

export function getWarrantyCustomerDataColumnDisplayName(
  columnKey: WarrantyCustomerDataColumnKey,
): string {
  return columnKeyToDisplayNameMap[columnKey];
}

export enum PurchasedExtendedWarrantyDataColumnKey {
  ORDER_ID = "orderId",
  COMPANY_NAME = "companyName",
  NAME = "name",
  ADDRESS1 = "address1",
  ADDRESS2 = "address2",
  CITY = "city",
  STATE = "state",
  COUNTRY = "country",
  ZIP = "zip",
  PHONE_NUMBER = "phoneNumber",
  EMAIL = "email",
  ORDER_DATE = "orderDate",
  DELIVERY_DATE = "deliveryDate",
  ITEM_PRICE = "itemPrice",
  ITEM_SKU = "itemSku",
  ITEM_NAME = "itemName",
  WARRANTY_UNIT_PRICE = "warrantyUnitPrice",
  WARRANTY_QUANTITY = "warrantyQuantity",
  WARRANTY_PRICE = "warrantyPrice",
  REV_SHARE_PRICE = "revSharePrice",
  WARRANTY_PURCHASE_DATE = "warrantyPurchaseDate",
  TERM_PURCHASED = "termPurchased",
  ACTIVATION_DATE = "activationDate",
  EXPIRATION_DATE = "expirationDate",
}

export function purchasedExtendedWarrantyDataColumnSorterSlow(
  a: PurchasedExtendedWarrantyDataColumnKey,
  b: PurchasedExtendedWarrantyDataColumnKey,
): number {
  return Object.values(PurchasedExtendedWarrantyDataColumnKey).indexOf(a) <
    Object.values(PurchasedExtendedWarrantyDataColumnKey).indexOf(b)
    ? -1
    : 1;
}

export function getPurchasedExtendedWarrantyDataColumnDisplayName(
  columnKey: PurchasedExtendedWarrantyDataColumnKey,
): string {
  switch (columnKey) {
    case PurchasedExtendedWarrantyDataColumnKey.ORDER_ID:
      return "Order ID";
    case PurchasedExtendedWarrantyDataColumnKey.COMPANY_NAME:
      return "Company Name";
    case PurchasedExtendedWarrantyDataColumnKey.NAME:
      return "Name";
    case PurchasedExtendedWarrantyDataColumnKey.ADDRESS1:
      return "Address 1";
    case PurchasedExtendedWarrantyDataColumnKey.ADDRESS2:
      return "Address 2";
    case PurchasedExtendedWarrantyDataColumnKey.CITY:
      return "City";
    case PurchasedExtendedWarrantyDataColumnKey.STATE:
      return "State";
    case PurchasedExtendedWarrantyDataColumnKey.COUNTRY:
      return "Country";
    case PurchasedExtendedWarrantyDataColumnKey.ZIP:
      return "Zip";
    case PurchasedExtendedWarrantyDataColumnKey.PHONE_NUMBER:
      return "Phone Number";
    case PurchasedExtendedWarrantyDataColumnKey.EMAIL:
      return "Email";
    case PurchasedExtendedWarrantyDataColumnKey.ORDER_DATE:
      return "Order Date";
    case PurchasedExtendedWarrantyDataColumnKey.DELIVERY_DATE:
      return "Delivery Date";
    case PurchasedExtendedWarrantyDataColumnKey.ITEM_PRICE:
      return "Item Price";
    case PurchasedExtendedWarrantyDataColumnKey.ITEM_SKU:
      return "Item SKU";
    case PurchasedExtendedWarrantyDataColumnKey.ITEM_NAME:
      return "Item Name";
    case PurchasedExtendedWarrantyDataColumnKey.WARRANTY_PRICE:
      return "Total Paid for Warranties";
    case PurchasedExtendedWarrantyDataColumnKey.WARRANTY_UNIT_PRICE:
      return "Warranty Unit Price";
    case PurchasedExtendedWarrantyDataColumnKey.WARRANTY_QUANTITY:
      return "Warranty Quantity";
    case PurchasedExtendedWarrantyDataColumnKey.WARRANTY_PURCHASE_DATE:
      return "Warranty Purchase Date";
    case PurchasedExtendedWarrantyDataColumnKey.TERM_PURCHASED:
      return "Term Purchased";
    case PurchasedExtendedWarrantyDataColumnKey.ACTIVATION_DATE:
      return "Activation Date";
    case PurchasedExtendedWarrantyDataColumnKey.EXPIRATION_DATE:
      return "Expiration Date";
    case PurchasedExtendedWarrantyDataColumnKey.REV_SHARE_PRICE:
      return "Estimated Rev Share";
    default:
      assertNever(columnKey);
  }
}

export function getReturnTableColumnDisplayName(
  columnKey: ReturnTableColumnKey,
): string {
  if (
    Object.values(ReturnDataColumnKey).includes(
      columnKey as ReturnDataColumnKey,
    )
  ) {
    return getReturnDataColumnDisplayName(columnKey as ReturnDataColumnKey);
  }
  if (
    Object.values(ReturnProductColumnKey).includes(
      columnKey as ReturnProductColumnKey,
    )
  ) {
    return getReturnedProductColumnDisplayName(
      columnKey as ReturnProductColumnKey,
    );
  }
  if (
    Object.values(OrderProductColumnKey).includes(
      columnKey as OrderProductColumnKey,
    )
  ) {
    return getOrderProductColumnDisplayName(columnKey as OrderProductColumnKey);
  }
  if (
    Object.values(PurchasedExtendedWarrantyDataColumnKey).includes(
      columnKey as PurchasedExtendedWarrantyDataColumnKey,
    )
  ) {
    return getPurchasedExtendedWarrantyDataColumnDisplayName(
      columnKey as PurchasedExtendedWarrantyDataColumnKey,
    );
  }
  return getWarrantyCustomerDataColumnDisplayName(
    columnKey as WarrantyCustomerDataColumnKey,
  );
}
